import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { formatCompactNumber } from '@/utils/formatters';
import { ApiService, type JobStatus } from '@/services/apiService';
import { api } from '@/services/api';

interface GenerationProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
  isOrderId?: boolean; // If true, use order status endpoint instead of legacy job status
}

export function GenerationProgressModal({
  isOpen,
  onClose,
  jobId,
  isOrderId = false
}: GenerationProgressModalProps) {
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<number | null>(null);
  const [lastTileCount, setLastTileCount] = useState<number>(0);
  const [downloadSpeed, setDownloadSpeed] = useState<number>(0);

  useEffect(() => {
    if (!isOpen || !jobId) {
      setJobStatus(null);
      setError(null);
      setStartTime(null);
      setLastUpdateTime(null);
      setLastTileCount(0);
      setDownloadSpeed(0);
      return;
    }

    // Poll for status (order or legacy job)
    const interval = setInterval(async () => {
      try {
        let status: JobStatus;

        if (isOrderId) {
          // Use order status endpoint for SaaS orders
          const orderStatus = await api.getOrderStatus(jobId);

          // Map order status to job status
          // Order: pending, paid, processing, completed, failed, canceled
          // Job: queued, processing, completed, failed
          let mappedStatus: JobStatus['status'] = 'queued';
          if (orderStatus.status === 'pending' || orderStatus.status === 'paid') {
            mappedStatus = 'queued';
          } else if (orderStatus.status === 'processing') {
            mappedStatus = 'processing';
          } else if (orderStatus.status === 'completed') {
            mappedStatus = 'completed';
          } else if (orderStatus.status === 'failed' || orderStatus.status === 'canceled') {
            mappedStatus = 'failed';
          }

          // Convert order status to JobStatus format
          status = {
            id: orderStatus.id,
            status: mappedStatus,
            progress: orderStatus.progress ?? 0,
            currentTile: (orderStatus.successfulTiles ?? 0) + (orderStatus.failedTiles ?? 0),
            totalTiles: orderStatus.totalTiles ?? 0,
            successfulTiles: orderStatus.successfulTiles ?? 0,
            failedTiles: orderStatus.failedTiles ?? 0,
            statusMessage: orderStatus.status === 'pending' ? 'Awaiting payment...' :
                          orderStatus.status === 'paid' ? 'Queued for processing...' :
                          orderStatus.status === 'processing' ? 'Downloading tiles...' : '',
            downloadUrl: orderStatus.downloadUrl || undefined,
            error: orderStatus.errorMessage || undefined,
            createdAt: '',
          };
        } else {
          // Use legacy job status endpoint
          status = await ApiService.getJobStatus(jobId);
        }

        const now = Date.now();

        // Initialize start time on first status update
        if (!startTime && status.status === 'processing') {
          setStartTime(now);
          setLastUpdateTime(now);
          setLastTileCount(status.successfulTiles || 0);
        }

        // Calculate download speed
        if (startTime && status.status === 'processing') {
          const currentTiles = status.successfulTiles || 0;

          // Calculate instantaneous speed (tiles per second over last update interval)
          if (lastUpdateTime && currentTiles > lastTileCount) {
            const timeDelta = (now - lastUpdateTime) / 1000; // seconds
            const tilesDelta = currentTiles - lastTileCount;
            const instantSpeed = tilesDelta / timeDelta;

            // Smooth the speed with exponential moving average (0.3 alpha for responsiveness)
            setDownloadSpeed(prev => prev === 0 ? instantSpeed : prev * 0.7 + instantSpeed * 0.3);
          }

          setLastUpdateTime(now);
          setLastTileCount(currentTiles);
        }

        setJobStatus(status);

        // Stop polling if job is complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Failed to fetch status:', err);
        setError('Failed to fetch status');
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds for orders

    return () => clearInterval(interval);
  }, [isOpen, jobId, isOrderId, startTime, lastUpdateTime, lastTileCount]);

  if (!isOpen || !jobId) return null;

  const handleDownload = () => {
    if (jobStatus?.downloadUrl) {
      // Download the actual MBTiles file
      const link = document.createElement('a');
      link.href = `http://localhost:3002${jobStatus.downloadUrl}`;
      link.download = `vortex-marine-chart-${jobId}.mbtiles`;
      link.click();
    }
    onClose();
  };

  // Format time duration (seconds to human-readable string)
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Format forecasted completion time
  const formatCompletionTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeStr = date.toLocaleTimeString('en-NZ', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // If today, just show time
    if (date.toDateString() === today.toDateString()) {
      return timeStr;
    }

    // If tomorrow, show "Tomorrow at [time]"
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${timeStr}`;
    }

    // Otherwise show full date
    return date.toLocaleDateString('en-NZ', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isGenerating = jobStatus?.status === 'queued' || jobStatus?.status === 'processing';
  const isComplete = jobStatus?.status === 'completed';
  const isFailed = jobStatus?.status === 'failed';
  const progress = jobStatus?.progress || 0;
  const totalTiles = jobStatus?.totalTiles || 0;
  const successfulTiles = jobStatus?.successfulTiles || 0;
  const failedTiles = jobStatus?.failedTiles || 0;
  const statusMessage = jobStatus?.statusMessage || '';

  // Calculate ETA
  const tilesRemaining = totalTiles - successfulTiles;
  const estimatedSecondsRemaining = downloadSpeed > 0 ? tilesRemaining / downloadSpeed : 0;
  const estimatedCompletionTime = startTime && downloadSpeed > 0
    ? Date.now() + (estimatedSecondsRemaining * 1000)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {isGenerating && (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <h2 className="text-2xl font-bold text-gray-900">Generating Your Chart</h2>
              </>
            )}
            {isComplete && (
              <>
                <div className="text-green-500 text-4xl">✓</div>
                <h2 className="text-2xl font-bold text-gray-900">Chart Ready!</h2>
              </>
            )}
            {isFailed && (
              <>
                <div className="text-red-500 text-4xl">✕</div>
                <h2 className="text-2xl font-bold text-gray-900">Generation Failed</h2>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {isGenerating && (
            <>
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-semibold text-primary-600">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-accent-500 h-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {/* Time Estimation */}
                {downloadSpeed > 0 && estimatedSecondsRemaining > 0 && estimatedCompletionTime && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-600">Time Remaining</span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        ~{formatDuration(estimatedSecondsRemaining)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-2">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-600">Expected Completion</span>
                      </div>
                      <span className="font-semibold text-primary-600">
                        {formatCompletionTime(estimatedCompletionTime)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs mt-1 text-gray-500">
                      <span>Download Speed</span>
                      <span>{Math.round(downloadSpeed)} tiles/sec</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Stats */}
              {totalTiles > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  <Card variant="bordered" padding="md" className="bg-green-50">
                    <div className="text-xs text-gray-600 mb-1">✓ Successful</div>
                    <div className="text-xl font-bold text-green-600">
                      {formatCompactNumber(successfulTiles)}
                    </div>
                  </Card>

                  <Card variant="bordered" padding="md" className="bg-red-50">
                    <div className="text-xs text-gray-600 mb-1">✕ Failed</div>
                    <div className="text-xl font-bold text-red-600">
                      {formatCompactNumber(failedTiles)}
                    </div>
                  </Card>

                  <Card variant="bordered" padding="md" className="bg-gray-50">
                    <div className="text-xs text-gray-600 mb-1">Total</div>
                    <div className="text-xl font-bold text-gray-900">
                      {formatCompactNumber(totalTiles)}
                    </div>
                  </Card>
                </div>
              )}

              {/* Status Message */}
              {statusMessage && (
                <div className="text-sm text-gray-600 text-center">
                  {statusMessage}
                </div>
              )}

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> You can close this window and we'll email you when your chart is ready.
                  This process may take a few minutes.
                </p>
              </div>
            </>
          )}

          {isComplete && (
            <>
              {/* Success Message */}
              <div className="text-center py-4">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Your marine chart is ready!
                </h3>
                <p className="text-gray-600">
                  {formatCompactNumber(successfulTiles)} tiles downloaded successfully
                  {failedTiles > 0 && (
                    <span className="text-orange-600"> ({failedTiles} tiles unavailable)</span>
                  )}
                </p>
              </div>

              {/* Download Stats */}
              <Card variant="bordered" padding="lg" className="bg-gradient-to-br from-primary-50 to-secondary-50">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Format</span>
                    <span className="font-semibold text-gray-900">MBTiles</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Compatible with</span>
                    <span className="font-semibold text-gray-900">OpenCPN, Navionics</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Offline Ready</span>
                    <span className="font-semibold text-green-600">✓ Yes</span>
                  </div>
                </div>
              </Card>

              {/* Instructions */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">📱 Next Steps</h4>
                <ol className="text-sm text-green-800 space-y-1 list-decimal list-inside">
                  <li>Download your MBTiles file</li>
                  <li>Transfer to your navigation device</li>
                  <li>Import into your chart plotter app</li>
                  <li>Navigate offline with confidence!</li>
                </ol>
              </div>
            </>
          )}

          {isFailed && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {jobStatus?.error || 'An unknown error occurred during generation.'}
              </p>
              <p className="text-sm text-red-700 mt-2">
                Please try again or contact support if the problem persists.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            {isGenerating && (
              <Button variant="outline" fullWidth onClick={onClose}>
                Continue in Background
              </Button>
            )}
            {isComplete && (
              <>
                <Button variant="outline" fullWidth onClick={onClose}>
                  Close
                </Button>
                <Button variant="accent" fullWidth onClick={handleDownload}>
                  Download MBTiles
                </Button>
              </>
            )}
            {isFailed && (
              <Button variant="outline" fullWidth onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
